import { LicenseLine } from "@/components/LicenseLine";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import type { Casino } from "@/lib/types";
import { AffiliateCTA } from "./AffiliateCTA";
import { RatingStars } from "./RatingStars";
import { BrandLogo } from "./BrandLogo";

type Props = {
  casino: Casino;
  rank?: number;
  source?: string;
};

export function CasinoCard({ casino, rank, source = "toplist" }: Props) {
  const { t, prefix, lang } = useI18n();
  const isSv = lang === "sv";
  const reviewBase = isSv ? `${prefix}/recensioner` : `${prefix}/reviews`;
  const intro = isSv ? casino.intro_sv : casino.intro_en;
  const pros = isSv ? casino.pros_sv : casino.pros_en;
  const bonus = isSv ? casino.bonus_summary_sv : casino.bonus_summary_en;
  const reviewHref = `${reviewBase}/${casino.slug}`;

  return (
    <article
      className={`relative overflow-hidden rounded-2xl border bg-white shadow-card transition hover:-translate-y-0.5 hover:shadow-card-hover ${
        casino.priority_brand ? "border-gold-300/70" : "border-ink-100 hover:border-brand-200"
      }`}
    >
      {casino.priority_brand && (
        <div className="absolute inset-x-0 top-0 flex items-center justify-between bg-gradient-to-r from-gold-300 via-gold-400 to-gold-500 px-4 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-ink-950">
          <span>★ {t("label.priorityBrand")}</span>
          {rank && <span>#{rank}</span>}
        </div>
      )}
      <div
        className={`grid gap-5 p-5 md:grid-cols-12 md:items-center ${
          casino.priority_brand ? "pt-9" : ""
        }`}
      >
        <div className="md:col-span-3">
          <div className="flex items-center gap-3">
            {!casino.priority_brand && rank && (
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand-50 text-sm font-extrabold text-brand-800">
                #{rank}
              </span>
            )}
            <BrandLogo
              slug={casino.slug}
              name={casino.name}
              src={casino.logo_url}
              size={64}
            />
            <div className="min-w-0">
              <h3 className="truncate text-lg font-bold text-ink-900">
                <Link to={reviewHref} className="text-ink-900 no-underline hover:text-brand-700">
                  {casino.name}
                </Link>
              </h3>
              <RatingStars value={casino.overall_rating} size="sm" />
            </div>
          </div>
        </div>

        <div className="md:col-span-5">
          {bonus ? (
            <p className="text-sm text-ink-800">
              <span className="font-semibold">
                {isSv ? "Bonus: " : "Bonus: "}
              </span>
              {bonus}
            </p>
          ) : (
            intro && <p className="text-sm text-ink-700">{intro}</p>
          )}
          <LicenseLine casino={casino} />
          <div className="mt-2 flex flex-wrap gap-1.5">
            {casino.swedish_license ? (
              <span className="pill-success">{t("label.swedishLicense")}</span>
            ) : (
              <span className="pill-warn">{t("label.noSwedishLicense")}</span>
            )}
            {casino.spelpaus_connected ? (
              <span className="pill-success">{t("label.spelpausConnected")}</span>
            ) : (
              <span className="pill-warn">{t("label.notSpelpaus")}</span>
            )}
            {casino.swish_supported && <span className="pill-brand">{t("label.swish")}</span>}
            {casino.bankid_supported && <span className="pill-brand">{t("label.bankid")}</span>}
            {casino.trustly_supported && <span className="pill-brand">{t("label.trustly")}</span>}
            {casino.zimpler_supported && <span className="pill-brand">{t("label.zimpler")}</span>}
            {casino.brite_supported && <span className="pill-brand">{t("label.brite")}</span>}
            {casino.crypto_supported && <span className="pill-brand">{t("label.crypto")}</span>}
          </div>
        </div>

        <div className="md:col-span-2">
          {pros && pros.length > 0 && (
            <ul className="space-y-1 text-sm text-ink-700">
              {pros.slice(0, 3).map((p) => (
                <li key={p} className="flex items-start gap-1.5">
                  <span aria-hidden className="mt-0.5 text-emerald-600">
                    ✓
                  </span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-col gap-2 md:col-span-2">
          <AffiliateCTA
            brandSlug={casino.slug}
            source={source}
            placement="card"
            size="md"
          />
          <Link to={reviewHref} className="btn-soft text-center text-sm">
            {t("cta.readReview")}
          </Link>
        </div>
      </div>
    </article>
  );
}
